import { Link } from "@inertiajs/react"
import { Coins } from "lucide-react"
import { useTranslation } from "react-i18next"

import { usePreviewMode } from "@/components/profile-preview/preview-mode"
import { RewardTeaserBar } from "@/components/rewards/reward-teaser-bar"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { creatorRewardsPath } from "@/routes"
import type { RewardTeaser } from "@/types/xp"

/**
 * OKS-513 — §11 coins wallet card (card 4, gold/premium).
 *
 * "{N} coins" + "Spendable · earned on approval" + a "Redeem →" button to
 * `/rewards`. When a `rewardTeaser` is present (the cheapest redeemable
 * reward the creator can't yet afford — TRD KD-12), a progress bar + "{N}
 * coins from the {reward}" teaser line render below; when null (no
 * out-of-reach reward) both are hidden.
 *
 * In read-only preview (a brand admin viewing the profile) the Redeem
 * affordance is inert (no navigation), matching the OKS-400 preview posture.
 */
interface CoinsWalletCardProps {
  coinBalance: number
  rewardTeaser: RewardTeaser | null
}

export function CoinsWalletCard({
  coinBalance,
  rewardTeaser,
}: CoinsWalletCardProps) {
  const { t } = useTranslation()
  const preview = usePreviewMode()

  return (
    <Card
      data-testid="coins-wallet-card"
      className="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100/40 dark:border-amber-900/40 dark:from-amber-950/30 dark:to-amber-900/10"
    >
      <CardContent className="flex flex-col gap-3 py-5">
        <div className="flex items-center gap-4">
          <Coins
            aria-hidden="true"
            className="size-8 shrink-0 text-amber-600"
          />
          <div className="flex flex-1 flex-col">
            <span
              data-testid="coins-wallet-value"
              className="text-2xl font-bold tabular-nums"
            >
              {t("profile.wallet.value", {
                value: formatCompactNumber(coinBalance),
              })}
            </span>
            <span className="text-muted-foreground text-sm">
              {t("profile.wallet.subtitle")}
            </span>
          </div>
          {preview ? (
            <Button
              variant="default"
              size="sm"
              disabled
              aria-disabled="true"
              data-testid="coins-wallet-redeem"
            >
              {t("profile.wallet.redeem")}
            </Button>
          ) : (
            <Button asChild variant="default" size="sm">
              <Link
                href={creatorRewardsPath()}
                data-testid="coins-wallet-redeem"
              >
                {t("profile.wallet.redeem")}
              </Link>
            </Button>
          )}
        </div>

        {rewardTeaser && (
          <RewardTeaserBar
            data-testid="coins-wallet-teaser"
            coinBalance={coinBalance}
            rewardTeaser={rewardTeaser}
          />
        )}
      </CardContent>
    </Card>
  )
}
